import { useState, useEffect, useCallback, useMemo } from 'react';
import { vacacionesApi, trabajadoresApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from '../../hooks/use-toast';
import {
  Plane, CalendarDays, Users, CheckCircle2, XCircle, Clock, Loader2,
  FileDown, Send, AlertTriangle, Ban, ShieldCheck,
} from 'lucide-react';

const RRHH_ROLES = ['admin', 'gerencia', 'rrhh', 'rrhh_encargado'];

const ESTADO_META = {
  pendiente_n1: { label: 'Pendiente Jefatura', cls: 'bg-amber-500/10 text-amber-600', icon: Clock },
  pendiente_n2: { label: 'Pendiente RRHH', cls: 'bg-blue-500/10 text-blue-600', icon: Clock },
  aprobada: { label: 'Aprobada', cls: 'bg-emerald-500/10 text-emerald-600', icon: CheckCircle2 },
  tomada: { label: 'Tomada', cls: 'bg-emerald-600/15 text-emerald-700', icon: CheckCircle2 },
  rechazada: { label: 'Rechazada', cls: 'bg-red-500/10 text-red-600', icon: XCircle },
  cancelada: { label: 'Cancelada', cls: 'bg-slate-400/10 text-slate-500', icon: Ban },
};

const EstadoBadge = ({ estado }) => {
  const m = ESTADO_META[estado] || { label: estado, cls: 'bg-slate-200 text-slate-600', icon: Clock };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${m.cls}`} data-testid={`estado-${estado}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
};

const fmtFecha = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}-${m}-${y}`;
};

const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  const days = [];
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(y, m - 1, d));
  return { desde: iso(first), hasta: iso(last), days, iso };
};

export default function VacacionesPage() {
  const { user } = useAuth();
  const isRRHH = useMemo(() => RRHH_ROLES.includes(user?.rol), [user]);

  const [tab, setTab] = useState('mis');
  const [miSaldo, setMiSaldo] = useState(null);
  const [sinFicha, setSinFicha] = useState(false);
  const [misSolicitudes, setMisSolicitudes] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form solicitud
  const [form, setForm] = useState({ fecha_inicio: '', fecha_fin: '', observacion: '', trabajador_numero: '' });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Planner
  const [plannerMes, setPlannerMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [plannerData, setPlannerData] = useState({ items: [] });

  const loadMis = useCallback(async () => {
    try {
      const res = await vacacionesApi.miSaldo();
      setMiSaldo(res.data);
      setSinFicha(false);
    } catch (err) {
      if (err?.response?.status === 404) { setSinFicha(true); setMiSaldo(null); }
    }
    try {
      // RRHH/Gerencia ven todas las solicitudes (scope backend); un trabajador ve sólo las suyas.
      const sols = await vacacionesApi.listSolicitudes({});
      setMisSolicitudes(sols.data);
    } catch { setMisSolicitudes([]); }
  }, []);

  const loadPendientes = useCallback(async () => {
    try { const res = await vacacionesApi.pendientes(); setPendientes(res.data.items || []); }
    catch { /* ignore */ }
  }, []);

  const loadSaldos = useCallback(async () => {
    try { const res = await vacacionesApi.saldos(); setSaldos(res.data || []); }
    catch { /* ignore */ }
  }, []);

  const loadPlanner = useCallback(async () => {
    const { desde, hasta } = monthRange(plannerMes);
    try { const res = await vacacionesApi.planner({ desde, hasta }); setPlannerData(res.data); }
    catch { /* ignore */ }
  }, [plannerMes]);

  useEffect(() => { loadMis(); loadPendientes(); }, [loadMis, loadPendientes]);
  useEffect(() => { if (isRRHH) loadSaldos(); }, [isRRHH, loadSaldos]);
  useEffect(() => { if (tab === 'saldos') loadSaldos(); }, [tab, loadSaldos]);
  useEffect(() => { if (tab === 'planner') loadPlanner(); }, [tab, loadPlanner]);
  useEffect(() => {
    if (isRRHH && trabajadores.length === 0) {
      trabajadoresApi.getAll({ activo: true }).then((r) => setTrabajadores(r.data)).catch(() => {});
    }
  }, [isRRHH, trabajadores.length]);

  // Live preview de días hábiles (con guard anti-respuesta obsoleta)
  useEffect(() => {
    if (form.fecha_inicio && form.fecha_fin && form.fecha_fin >= form.fecha_inicio) {
      let ignore = false;
      vacacionesApi.calcularDias({ inicio: form.fecha_inicio, fin: form.fecha_fin })
        .then((r) => { if (!ignore) setPreview(r.data); })
        .catch(() => { if (!ignore) setPreview(null); });
      return () => { ignore = true; };
    }
    setPreview(null);
  }, [form.fecha_inicio, form.fecha_fin]);

  const disponibleObjetivo = useMemo(() => {
    if (form.trabajador_numero) {
      const s = saldos.find((x) => String(x.trabajador_numero) === String(form.trabajador_numero));
      return s ? s.disponible : null;
    }
    return miSaldo ? miSaldo.disponible : null;
  }, [form.trabajador_numero, saldos, miSaldo]);

  const sobregiro = preview && disponibleObjetivo !== null && preview.dias_habiles > disponibleObjetivo;

  const handleCrear = async () => {
    if (!form.fecha_inicio || !form.fecha_fin) { toast({ title: 'Indica fecha de inicio y término', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = { fecha_inicio: form.fecha_inicio, fecha_fin: form.fecha_fin, observacion: form.observacion };
      if (isRRHH && form.trabajador_numero) payload.trabajador_numero = parseInt(form.trabajador_numero);
      await vacacionesApi.crearSolicitud(payload);
      toast({ title: 'Solicitud enviada', description: sobregiro ? 'Queda sujeta a autorización por sobregiro.' : 'En flujo de aprobación.' });
      setForm({ fecha_inicio: '', fecha_fin: '', observacion: '', trabajador_numero: '' });
      setPreview(null);
      loadMis(); loadPendientes();
      if (tab === 'saldos') loadSaldos();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'No se pudo crear la solicitud', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const doAccion = async (fn, id, okMsg) => {
    try { await fn(id); toast({ title: okMsg }); loadMis(); loadPendientes(); if (tab === 'saldos') loadSaldos(); }
    catch (err) { toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' }); }
  };

  const aprobar = (item) => {
    const fn = item.mi_nivel === 1 ? vacacionesApi.aprobarN1 : vacacionesApi.aprobarN2;
    doAccion((id) => fn(id, {}), item.id, item.mi_nivel === 1 ? 'Nivel 1 confirmado' : 'Solicitud autorizada');
  };
  const rechazar = (item) => {
    const comentario = window.prompt('Motivo del rechazo (opcional):');
    if (comentario === null) return; // Cancelado por el usuario
    doAccion((id) => vacacionesApi.rechazar(id, { comentario }), item.id, 'Solicitud rechazada');
  };
  const cancelar = (item) => {
    if (!window.confirm('¿Cancelar esta solicitud de vacaciones?')) return;
    doAccion((id) => vacacionesApi.cancelar(id), item.id, 'Solicitud cancelada');
  };

  const showAprob = isRRHH || pendientes.length > 0;
  const showGestion = isRRHH || pendientes.length > 0; // jefatura (tiene pendientes) o RRHH

  const tabs = [
    { id: 'mis', label: 'Mis Vacaciones', icon: Plane, show: true },
    { id: 'aprob', label: `Aprobaciones${pendientes.length ? ` (${pendientes.length})` : ''}`, icon: ShieldCheck, show: showAprob },
    { id: 'saldos', label: 'Saldos', icon: Users, show: showGestion },
    { id: 'planner', label: 'Planificador', icon: CalendarDays, show: true },
  ].filter((t) => t.show);

  return (
    <div className="space-y-4 page-enter" data-testid="vacaciones-page">
      <div className="flex items-center gap-2">
        <Plane className="w-5 h-5 text-blue-600" />
        <h1 className="text-xl font-semibold">Vacaciones y Feriado Legal</h1>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ===== MIS VACACIONES ===== */}
      {tab === 'mis' && (
        <div className="space-y-4">
          {sinFicha && !isRRHH && (
            <Card><CardContent className="p-4 text-sm text-amber-700 bg-amber-50 flex items-center gap-2" data-testid="sin-ficha-notice">
              <AlertTriangle className="w-4 h-4" /> Tu usuario no está vinculado a una ficha de trabajador. Pide a RRHH que registre tu correo o RUT en tu ficha.
            </CardContent></Card>
          )}

          {miSaldo && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card data-testid="card-disponible"><CardContent className="p-4">
                <div className="text-xs text-muted-foreground">Saldo disponible</div>
                <div className={`text-2xl font-bold ${miSaldo.disponible < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{miSaldo.disponible}</div>
                <div className="text-[11px] text-muted-foreground">días hábiles</div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-xs text-muted-foreground">Devengado</div>
                <div className="text-2xl font-bold">{miSaldo.devengado}</div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-xs text-muted-foreground">Tomados</div>
                <div className="text-2xl font-bold">{miSaldo.usados}</div>
              </CardContent></Card>
              <Card><CardContent className="p-4">
                <div className="text-xs text-muted-foreground">Derecho anual</div>
                <div className="text-2xl font-bold">{miSaldo.annual_total}</div>
                <div className="text-[11px] text-muted-foreground">{miSaldo.zona_extrema ? 'Zona extrema' : 'Estándar'}{miSaldo.progresivo ? ` · +${miSaldo.progresivo} prog.` : ''}</div>
              </CardContent></Card>
            </div>
          )}

          {miSaldo?.alerta_dt && (
            <div className="border border-amber-300 rounded-lg p-3 bg-amber-50 text-amber-800 text-xs flex items-center gap-2" data-testid="alerta-dt">
              <AlertTriangle className="w-4 h-4" /> <b>Alerta DT:</b> acumulas 2 o más períodos de feriado sin tomar. La Dirección del Trabajo recomienda regularizarlo.
            </div>
          )}

          {(miSaldo || isRRHH) && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Solicitar vacaciones</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {isRRHH && (
                    <div className="space-y-1 lg:col-span-2">
                      <Label className="text-xs">Trabajador {miSaldo ? '(vacío = yo)' : '(requerido)'}</Label>
                      <select value={form.trabajador_numero} onChange={(e) => setForm({ ...form, trabajador_numero: e.target.value })}
                        className="w-full h-9 px-2 text-sm rounded-md border border-input bg-background" data-testid="select-trabajador">
                        <option value="">{miSaldo ? '— Yo mismo —' : '— Seleccionar —'}</option>
                        {trabajadores.map((t) => <option key={t.numero} value={t.numero}>{t.numero} · {t.nombre}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Desde</Label>
                    <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className="rounded-xl" data-testid="input-fecha-inicio" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Hasta</Label>
                    <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} className="rounded-xl" data-testid="input-fecha-fin" />
                  </div>
                  <div className="space-y-1 lg:col-span-2">
                    <Label className="text-xs">Observación</Label>
                    <Input value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} className="rounded-xl" data-testid="input-observacion" />
                  </div>
                </div>
                {preview && (
                  <div className="mt-3 text-sm flex flex-wrap items-center gap-3" data-testid="preview-dias">
                    <span className="font-semibold">{preview.dias_habiles} día(s) hábiles</span>
                    {preview.feriados_en_rango?.length > 0 && <span className="text-xs text-muted-foreground">({preview.feriados_en_rango.length} feriado(s) descontado(s))</span>}
                    <span className="text-xs text-muted-foreground">Reintegro: {fmtFecha(preview.fecha_reintegro)}</span>
                    {sobregiro && <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium" data-testid="warn-sobregiro"><AlertTriangle className="w-3.5 h-3.5" /> Excede el saldo ({disponibleObjetivo}); requiere autorización Gerencia/RRHH</span>}
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <Button onClick={handleCrear} disabled={saving} className="rounded-xl gap-1.5" data-testid="btn-crear-solicitud">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar solicitud
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{isRRHH ? 'Solicitudes' : 'Mis solicitudes'}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    {isRRHH && <TableHead>Trabajador</TableHead>}
                    <TableHead>Período</TableHead><TableHead className="text-center">Días</TableHead>
                    <TableHead>Estado</TableHead><TableHead>Observación</TableHead><TableHead className="w-28" />
                  </TableRow></TableHeader>
                  <TableBody>
                    {misSolicitudes.length === 0 ? (
                      <TableRow><TableCell colSpan={isRRHH ? 6 : 5} className="text-center py-8 text-muted-foreground">Sin solicitudes</TableCell></TableRow>
                    ) : misSolicitudes.map((s) => (
                      <TableRow key={s.id} data-testid={`mi-solicitud-${s.id}`}>
                        {isRRHH && <TableCell className="text-sm font-medium">{s.trabajador_nombre}</TableCell>}
                        <TableCell className="text-sm">{fmtFecha(s.fecha_inicio)} → {fmtFecha(s.fecha_fin)}{s.sobregiro && <span className="ml-1 text-[10px] text-red-600 font-semibold">(sobregiro)</span>}</TableCell>
                        <TableCell className="text-center tabular-nums">{s.dias_habiles}</TableCell>
                        <TableCell><EstadoBadge estado={s.estado} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{s.observacion || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            {(s.estado === 'aprobada' || s.estado === 'tomada') && (
                              <Button size="icon" variant="ghost" className="h-8 w-8" title="Comprobante PDF"
                                onClick={() => window.open(vacacionesApi.comprobanteUrl(s.id), '_blank')} data-testid={`btn-pdf-${s.id}`}>
                                <FileDown className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {['pendiente_n1', 'pendiente_n2', 'aprobada'].includes(s.estado) && (
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Cancelar"
                                onClick={() => cancelar(s)} data-testid={`btn-cancelar-${s.id}`}>
                                <Ban className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== APROBACIONES ===== */}
      {tab === 'aprob' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Solicitudes por aprobar</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Trabajador</TableHead><TableHead>Período</TableHead><TableHead className="text-center">Días</TableHead>
                  <TableHead>Nivel</TableHead><TableHead>Estado</TableHead><TableHead className="w-48" />
                </TableRow></TableHeader>
                <TableBody>
                  {pendientes.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No hay solicitudes pendientes</TableCell></TableRow>
                  ) : pendientes.map((s) => (
                    <TableRow key={s.id} data-testid={`pendiente-${s.id}`}>
                      <TableCell className="text-sm font-medium">{s.trabajador_nombre}<div className="text-[11px] text-muted-foreground">{s.trabajador_rut}</div></TableCell>
                      <TableCell className="text-sm">{fmtFecha(s.fecha_inicio)} → {fmtFecha(s.fecha_fin)}{s.sobregiro && <span className="ml-1 text-[10px] text-red-600 font-semibold">(sobregiro)</span>}</TableCell>
                      <TableCell className="text-center tabular-nums">{s.dias_habiles}</TableCell>
                      <TableCell className="text-xs">{s.mi_nivel === 1 ? 'Jefatura (N1)' : 'RRHH (N2)'}</TableCell>
                      <TableCell><EstadoBadge estado={s.estado} /></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5 justify-end">
                          <Button size="sm" className="rounded-xl gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => aprobar(s)} data-testid={`btn-aprobar-${s.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> {s.mi_nivel === 1 ? 'Confirmar' : 'Autorizar'}
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-xl gap-1 text-red-600" onClick={() => rechazar(s)} data-testid={`btn-rechazar-${s.id}`}>
                            <XCircle className="h-3.5 w-3.5" /> Rechazar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== SALDOS ===== */}
      {tab === 'saldos' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Saldos del personal</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Trabajador</TableHead><TableHead>Cargo / Área</TableHead>
                  <TableHead className="text-center">Derecho anual</TableHead><TableHead className="text-center">Devengado</TableHead>
                  <TableHead className="text-center">Tomados</TableHead><TableHead className="text-center">Comprometidos</TableHead><TableHead className="text-center">Disponible</TableHead><TableHead />
                </TableRow></TableHeader>
                <TableBody>
                  {saldos.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Sin datos</TableCell></TableRow>
                  ) : saldos.map((s) => (
                    <TableRow key={s.trabajador_numero} data-testid={`saldo-${s.trabajador_numero}`}>
                      <TableCell className="text-sm font-medium">{s.trabajador_nombre}
                        {s.alerta_dt && <AlertTriangle className="inline w-3.5 h-3.5 ml-1 text-amber-500" title="Alerta DT: 2+ períodos sin tomar" />}
                        <div className="text-[11px] text-muted-foreground">{s.rut}</div>
                      </TableCell>
                      <TableCell className="text-xs">{s.cargo || '-'} / {s.area || '-'}</TableCell>
                      <TableCell className="text-center tabular-nums">{s.annual_total}{s.zona_extrema ? ' ZE' : ''}</TableCell>
                      <TableCell className="text-center tabular-nums">{s.devengado}</TableCell>
                      <TableCell className="text-center tabular-nums">{s.usados}</TableCell>
                      <TableCell className="text-center tabular-nums text-amber-600" title="Días reservados en solicitudes pendientes">{s.reservados}</TableCell>
                      <TableCell className={`text-center tabular-nums font-bold ${s.disponible < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{s.disponible}</TableCell>
                      <TableCell>
                        {isRRHH && (
                          <Button size="sm" variant="outline" className="rounded-xl text-xs" data-testid={`btn-solicitar-${s.trabajador_numero}`}
                            onClick={() => { setForm({ fecha_inicio: '', fecha_fin: '', observacion: '', trabajador_numero: String(s.trabajador_numero) }); setTab('mis'); }}>
                            Solicitar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== PLANIFICADOR (Gantt) ===== */}
      {tab === 'planner' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base">Planificador de vacaciones</CardTitle>
              <Input type="month" value={plannerMes} onChange={(e) => setPlannerMes(e.target.value)} className="h-9 w-40 rounded-xl" data-testid="input-planner-mes" />
            </div>
          </CardHeader>
          <CardContent>
            <GanttPlanner mes={plannerMes} data={plannerData} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function GanttPlanner({ mes, data }) {
  const { days } = monthRange(mes);
  const items = data?.items || [];
  const rows = useMemo(() => {
    const byWorker = {};
    for (const it of items) {
      if (!byWorker[it.trabajador_numero]) byWorker[it.trabajador_numero] = { nombre: it.trabajador_nombre, numero: it.trabajador_numero, spans: [] };
      byWorker[it.trabajador_numero].spans.push(it);
    }
    return Object.values(byWorker).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [items]);

  const estadoCls = (estado) => ({
    aprobada: 'bg-emerald-500', tomada: 'bg-emerald-600',
    pendiente_n1: 'bg-amber-400', pendiente_n2: 'bg-blue-400',
  }[estado] || 'bg-slate-400');

  const dayIso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

  if (rows.length === 0) return <div className="text-sm text-muted-foreground py-8 text-center" data-testid="planner-empty">Sin vacaciones planificadas en este mes.</div>;

  return (
    <div className="overflow-x-auto" data-testid="gantt-planner">
      <div className="min-w-max">
        {/* Header de días */}
        <div className="flex">
          <div className="w-44 shrink-0 text-xs font-semibold text-muted-foreground px-2 py-1">Trabajador</div>
          {days.map((d) => {
            const wk = d.getDay() === 0 || d.getDay() === 6;
            return <div key={d.getDate()} className={`w-6 shrink-0 text-center text-[10px] py-1 ${wk ? 'text-slate-300' : 'text-muted-foreground'}`}>{d.getDate()}</div>;
          })}
        </div>
        {/* Filas */}
        {rows.map((row) => (
          <div key={row.numero} className="flex items-center border-t" data-testid={`gantt-row-${row.numero}`}>
            <div className="w-44 shrink-0 text-xs font-medium px-2 py-1.5 truncate" title={row.nombre}>{row.nombre}</div>
            {days.map((d) => {
              const iso = dayIso(d);
              const span = row.spans.find((s) => iso >= s.fecha_inicio.slice(0, 10) && iso <= s.fecha_fin.slice(0, 10));
              const wk = d.getDay() === 0 || d.getDay() === 6;
              return (
                <div key={iso} className={`w-6 h-6 shrink-0 border-l ${wk ? 'bg-slate-50' : ''}`}>
                  {span && <div className={`w-full h-full ${estadoCls(span.estado)}`} title={`${span.estado} · ${row.nombre}`} />}
                </div>
              );
            })}
          </div>
        ))}
        {/* Leyenda */}
        <div className="flex gap-3 mt-3 text-[11px] text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> Aprobada</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" /> Pend. Jefatura</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" /> Pend. RRHH</span>
        </div>
      </div>
    </div>
  );
}
